import { iterateObject } from '@perch-labs/warbler';
import { action as sbAction } from '@storybook/addon-actions';
import { set } from 'lodash';
import { BillingDataSource } from '~/typings/context';
import { TEST_DATA } from './test-data';

export const action = sbAction;

export const hideControl = {
  table: { disable: true },
  control: { disable: true },
};

export const hideScreenControls = {
  context: hideControl,
  correspondingState: hideControl,
  goTo: hideControl,
  overrideCopy: hideControl,
  next: hideControl,
  skip: hideControl,
  step: hideControl,
};

export const isStorybook = () => location.href.includes('story');

export const removeEmptyArgs = (args: Record<string, any>) =>
  iterateObject(args, {
    filterByValue: value => ![null, undefined, ''].includes(value),
  });

/*
  gross alert, but this util will:
  - find namespaced args (e.g. context.zipCode)
  - remove null, undefined, and empty string values
  - return an object without the namespace
  - turn nested paths into objects:
    context.utility.displayName -> { utility: { displayName: 'value' }}
*/
export const getArgsFor = (namespace = '', args: Record<string, any> = {}) =>
  Object.entries(args).reduce((out, [key, value]) => {
    if (!key.startsWith(`${namespace}.`)) return out;
    if ([null, undefined, ''].includes(value)) return out;

    const path = key.replace(`${namespace}.`, '');
    set(out, path, value);
    return out;
  }, {});

export const getGroupControls = (namespace: string, fields: string[]) =>
  fields.reduce((out, field) => {
    out[`${namespace}.${field}`] = {
      table: { category: namespace },
      name: field,
    };
    return out;
  }, {});

export const extractGroupControls = (args: Record<string, any>, groups: string[]) => {
  const groupedList = groups.map(group => {
    const changed = iterateObject(args, {
      filterByKey: key => key.startsWith(group),
      mapKeys: key => key.replace(`${group}.`, ''),
    });

    return getGroupControls(group, Object.keys(changed));
  });
  return Object.assign({}, ...groupedList);
};

export const getScreenProps = <Context extends object>(args: Record<string, any>) => ({
  childStep: args.childStep,
  context: getArgsFor('context', args) as Context,
  error: args.error,
  goTo: args.goTo || {},
  loading: args.loading || false,
  meta: {},
  next: sbAction('next'),
  overrideCopy: () => {
    return { ...getArgsFor('copy', args) };
  },
  pending: args.pending,
  skip: args.skip ? () => true : undefined,
  step: '',
});

export const getOverrideCopy = (args: Record<string, any>) => {
  return () => ({ ...getArgsFor('copy', args) });
};

export const utilityControl = (startingValue: keyof typeof TEST_DATA.utilities = 'natGrid') => ({
  'context.utility': {
    name: 'utility',
    table: {
      category: 'context',
    },
    options: Object.keys(TEST_DATA.utilities),
    mapping: TEST_DATA.utilities,
    control: {
      type: 'select',
      defaultValue: startingValue,
    },
  },
});

export const billingSelectionControl = (startingValue: BillingDataSource = 'acb') => ({
  'context.billingSelection': {
    name: 'billingSelection',
    table: {
      category: 'context',
    },
    control: {
      type: 'select',
      options: ['acb', 'dual', 'ucb'] as BillingDataSource[],
      defaultValue: startingValue,
    },
  },
});

export const carouselSelectionControl = (startingValue: String) => ({
  'copy.carouselIndex': {
    name: 'carouselIndex',
    table: {
      category: 'copy',
    },
    control: {
      type: 'select',
      options: [
        'GET_STARTED_CAROUSEL',
        'SOLAR_WITHOUT_ROOF_CAROUSEL',
        'HOW_IT_WORKS_CAROUSEL',
        'READY_TO_SUBSCRIBE_CAROUSEL',
      ] as String[],
      defaultValue: startingValue,
    },
  },
});

export const screenImageSelectionControl = (startingValue: String) => ({
  'copy.imageType': {
    name: 'imageType',
    table: {
      category: 'copy',
    },
    control: {
      type: 'select',
      options: [
        'GET_STARTED',
        'SOLAR_WITHOUT_A_ROOF',
        'HOW_IT_WORKS',
        'READY_TO_SUBSCRIBE',
      ] as String[],
      defaultValue: startingValue,
    },
  },
});
